import { FC, useMemo, useState } from 'react';

import { GiftCouponForRedemptionDashboard } from '@shared/api/giftCoupon/giftCoupon.interface';

import { getItemName } from '../utils/redemption.utils';
import { undeliverCountOf } from '../utils/undeliver.utils';
import { CsMemoCouponCell } from './csMemo';

const fmtTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${m}.${day} ${h}:${min}`;
};

const fmtPhone = (phone: string | undefined) => {
  if (!phone) return '-';
  const n = phone.replace(/[^0-9]/g, '');
  if (n.length === 11) return `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}`;
  return phone;
};

/** 상품 - 선택유닛 */
const productLabel = (c: GiftCouponForRedemptionDashboard): string => {
  const itemName = getItemName(c);
  const unit = c.selectedChoiceUnit;
  const unitName = unit && typeof unit === 'object' ? unit.name : '';
  return unitName ? `${itemName} - ${unitName}` : itemName;
};

const ordererOf = (c: GiftCouponForRedemptionDashboard) => {
  const order = typeof c.giftOrder === 'object' ? c.giftOrder : null;
  return order?.bugo?.member ?? null;
};

/** 보류된 지 며칠 — 오래 묵은 건을 눈에 띄게 한다 */
const heldDays = (at: Date | string | undefined): number | null => {
  if (!at) return null;
  const diff = Date.now() - new Date(at).getTime();
  return diff < 0 ? 0 : Math.floor(diff / (24 * 60 * 60 * 1000));
};

interface HoldTabProps {
  items: GiftCouponForRedemptionDashboard[];
  /** 보류 해제 (발송대기로 복귀) — 확인창·토스트는 호출부가 담당 */
  onUnhold: (couponIds: string[]) => Promise<boolean>;
  /** 발송처리 모달 열기 — 보류 상태에서도 그대로 발송할 수 있다 */
  onOpenDeliverModal: (couponId: string) => void;
  copyToClipboard: (text: string) => void;
}

/**
 * 발송보류 탭 — 발송취소하며 "나중에 처리"로 빼둔 건을 모아 본다.
 * 스펙: docs/gift-coupon-onhold-spec.md
 *
 * **보류는 발송 금지가 아니라 대기 큐에서 빼두는 것**이 목적이다 — 계속 실패하는 건을
 * 운영자들이 발송대기에서 반복해서 붙잡지 않게 하려는 것. 다른 경로로 발송에 성공하면
 * 여기서 바로 발송처리할 수 있고, 정상 흐름으로 되돌리려면 발송대기로 복귀시킨다.
 */
export const HoldTab: FC<HoldTabProps> = ({
  items,
  onUnhold,
  onOpenDeliverModal,
  copyToClipboard,
}) => {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // 목록이 바뀌면(해제·신규 보류) 사라진 선택은 자동으로 정리한다
  const visibleSelectedIds = useMemo(
    () => items.filter((c) => selectedIds.has(c._id)).map((c) => c._id),
    [items, selectedIds],
  );

  const handleUnhold = async (couponIds: string[]) => {
    setBusyId(couponIds[0] ?? null);
    try {
      const done = await onUnhold(couponIds);
      if (done) setSelectedIds(new Set());
    } finally {
      setBusyId(null);
    }
  };

  const toggle = (couponId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(couponId)) next.delete(couponId);
      else next.add(couponId);
      return next;
    });
  };

  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white py-12 text-center text-sm text-gray-400">
        발송보류 중인 건이 없습니다.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="rounded-lg bg-amber-50 px-3.5 py-3 text-sm leading-relaxed text-amber-800">
        발송취소하면서 <span className="font-bold">나중에 처리</span>로 빼둔 건입니다.
        발송대기 목록·건수·충전 예상액에는 잡히지 않습니다.
        <br />
        다른 방법으로 발송에 성공했다면 여기서 바로{' '}
        <span className="font-bold">발송처리</span>하면 되고, 정상 흐름으로 되돌리려면{' '}
        <span className="font-bold">발송대기로 복귀</span>를 누르세요 —
        신청정보(수령자·상품)는 그대로 유지됩니다.
      </p>

      {visibleSelectedIds.length > 0 && (
        <div className="flex items-center gap-3 rounded-lg bg-amber-100 px-4 py-2.5">
          <span className="text-sm font-medium text-amber-900">
            {visibleSelectedIds.length}건 선택됨
          </span>
          <button
            onClick={() => handleUnhold(visibleSelectedIds)}
            disabled={busyId !== null}
            className="rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-xs font-bold text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
          >
            발송대기로 복귀 ({visibleSelectedIds.length})
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            선택 해제
          </button>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="w-10 px-3 py-2" />
              <th className="whitespace-nowrap px-4 py-2 font-medium">상품</th>
              <th className="whitespace-nowrap px-4 py-2 font-medium">코드</th>
              <th className="whitespace-nowrap px-4 py-2 font-medium">수령자</th>
              <th className="whitespace-nowrap px-4 py-2 font-medium">주문자(상주)</th>
              <th className="whitespace-nowrap px-4 py-2 font-medium">보류일시</th>
              <th
                className="whitespace-nowrap px-4 py-2 font-medium"
                title="CS 응대 메모 (클릭 시 메모장)"
              >
                CS메모
              </th>
              <th className="whitespace-nowrap px-4 py-2 font-medium">처리</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((c) => {
              const orderer = ordererOf(c);
              const days = heldDays(c.hold?.at);
              const undeliverCount = undeliverCountOf(c);
              return (
                <tr key={c._id} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c._id)}
                      onChange={() => toggle(c._id)}
                      className="h-4 w-4 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-2.5 text-gray-800">
                    {productLabel(c)}
                    {undeliverCount > 0 && (
                      <span
                        className="ml-1.5 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-bold text-gray-500"
                        title="발송완료 취소 누적 횟수"
                      >
                        취소 {undeliverCount}회
                      </span>
                    )}
                    {c.hold?.reason && (
                      <p className="mt-0.5 text-xs text-amber-700">{c.hold.reason}</p>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 font-mono text-gray-600">
                    {c.fullCode || c.code}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-700">
                    <button
                      onClick={() =>
                        copyToClipboard(
                          (c.recipient?.phoneNumber || '').replace(/[^0-9]/g, ''),
                        )
                      }
                      className="hover:underline"
                      title="번호 복사"
                    >
                      {fmtPhone(c.recipient?.phoneNumber)}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-700">
                    {orderer ? (
                      <button
                        onClick={() =>
                          copyToClipboard(
                            (orderer.phoneNumber || '').replace(/[^0-9]/g, ''),
                          )
                        }
                        className="text-left hover:underline"
                        title="주문자 번호 복사 (주문 검색에 붙여넣기)"
                      >
                        {orderer.fullName}
                        <span className="ml-1 text-gray-400">
                          {fmtPhone(orderer.phoneNumber)}
                        </span>
                      </button>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">
                    {fmtTime(c.hold?.at)}
                    {days !== null && (
                      <span
                        className={`ml-1.5 rounded px-1.5 py-0.5 text-[11px] font-bold ${
                          days >= 7
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {days === 0 ? '오늘' : `${days}일 경과`}
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <CsMemoCouponCell
                      coupon={c}
                      subtitle={
                        orderer
                          ? `${orderer.fullName} · ${fmtPhone(orderer.phoneNumber)}`
                          : undefined
                      }
                    />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <div className="flex gap-2">
                      <button
                        onClick={() => onOpenDeliverModal(c._id)}
                        className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-700"
                        title="보류 상태 그대로 발송 처리합니다"
                      >
                        발송처리
                      </button>
                      <button
                        onClick={() => handleUnhold([c._id])}
                        disabled={busyId !== null}
                        className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        title="발송대기 목록으로 되돌립니다"
                      >
                        발송대기로 복귀
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
